import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import AdminHeader from '../layout/AdminHeader';
import SuperAdminSidebar from '../layout/SuperAdminSidebar';
import { useAuthStore } from '@/stores/auth-store';
import { useSidebar } from '@/contexts/SidebarContext';

/**
 * Shell for the super-admin platform console (`/accounts`, `/accounts/:id`,
 * `/token-usage`) — mounted on the `admin.` host in place of the
 * tenant-admin flow (see App.tsx). Mirrors AdminLayout's structure and
 * reuses AdminHeader directly (with `homeHref="/accounts"`); only the
 * sidebar is a dedicated, deliberately thin component (SuperAdminSidebar)
 * since the nav surface (Accounts, Token Usage) has nothing in common with
 * AdminSidebar's tenant/enterprise nav. Mounted behind <SuperAdminGate> in
 * App.tsx, so children need no per-route guard.
 */
export default function SuperAdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isCollapsed, toggleCollapse } = useSidebar();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const userData = {
    name: user?.name || 'Super Admin',
    email: user?.email || '',
    avatar: null,
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-tutara-dark-mode-bg">
      <AdminHeader onMenuClick={() => setSidebarOpen(!sidebarOpen)} homeHref="/accounts" />

      <div className="flex">
        <SuperAdminSidebar
          isOpen={sidebarOpen}
          isCollapsed={isCollapsed}
          onClose={() => setSidebarOpen(false)}
          onToggleCollapse={toggleCollapse}
          user={userData}
          onLogout={handleLogout}
        />

        <main className={`flex-1 pt-16 transition-all duration-300 overflow-x-hidden ${isCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
          <div className="px-4 sm:px-6 lg:px-8 py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
